import { z } from 'zod';
import { daysInclusive, parts, type ISODate } from './date';
import { zDate, zId } from './contracts/common';

/**
 * KI-Assistent ("Nebenkosten-Berater", docs/17-ki-assistent.md): contracts, file types, the own-work calculator and
 * the tip engine. Everything here is pure; the server loads data and talks to the AI provider, web and mobile render.
 *
 * Ground rule (ADR-0007, ADR-0011): the assistant proposes, the person confirms. Writes are proposals that run through
 * the regular API; only a person who switched on "Direkt eintragen" gets internal changes applied at once, and every
 * change is logged and can be undone.
 */

// ───────────── consent ─────────────

/** Bump when the consent text of the assistant changes materially (asks everybody again). */
export const AGENT_CONSENT_VERSION = 1;
type ConsentLike = { granted: boolean; version: number } | null | undefined;
export const hasAgentConsent = (c: ConsentLike) => !!c?.granted && c.version >= AGENT_CONSENT_VERSION;

// ───────────── limits ─────────────

export const AGENT_LIMITS = {
  /** Characters of one message. */
  textMax: 4000,
  /** Files attached to one message. */
  filesPerMessage: 5,
  /** Size of one file (base64 upload through the JSON API, express limit is 12 MB). */
  fileMaxBytes: 8 * 1024 * 1024,
  /** Extracted text of one file sent to the model (characters). */
  fileTextMax: 60_000,
  /** Previous messages sent with every turn. */
  historyMessages: 16,
  /** Tool rounds per turn before the assistant has to answer. */
  toolRounds: 5,
  /** Characters of a tool result sent back to the model. */
  toolResultMax: 12_000,
} as const;

// ───────────── files ─────────────

/** How the server reads a file: natively by the model (pdf, image) or as extracted text. */
export type AgentFileKind = 'pdf' | 'image' | 'text' | 'office';
/** Accepted chat attachments (extension → mime, kind). HEIC is converted by the clients before upload. */
export const AGENT_FILE_TYPES = {
  pdf: { mime: 'application/pdf', kind: 'pdf' },
  jpg: { mime: 'image/jpeg', kind: 'image' },
  jpeg: { mime: 'image/jpeg', kind: 'image' },
  png: { mime: 'image/png', kind: 'image' },
  webp: { mime: 'image/webp', kind: 'image' },
  gif: { mime: 'image/gif', kind: 'image' },
  txt: { mime: 'text/plain', kind: 'text' },
  md: { mime: 'text/markdown', kind: 'text' },
  csv: { mime: 'text/csv', kind: 'text' },
  tsv: { mime: 'text/tab-separated-values', kind: 'text' },
  json: { mime: 'application/json', kind: 'text' },
  xml: { mime: 'application/xml', kind: 'text' },
  html: { mime: 'text/html', kind: 'text' },
  htm: { mime: 'text/html', kind: 'text' },
  eml: { mime: 'message/rfc822', kind: 'text' },
  rtf: { mime: 'application/rtf', kind: 'text' },
  docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', kind: 'office' },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', kind: 'office' },
  pptx: { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', kind: 'office' },
  odt: { mime: 'application/vnd.oasis.opendocument.text', kind: 'office' },
  ods: { mime: 'application/vnd.oasis.opendocument.spreadsheet', kind: 'office' },
} as const satisfies Record<string, { mime: string; kind: AgentFileKind }>;
export type AgentFileExt = keyof typeof AGENT_FILE_TYPES;
export const AGENT_FILE_EXTS = Object.keys(AGENT_FILE_TYPES) as AgentFileExt[];
/** `accept` attribute of the file picker. */
export const AGENT_FILE_ACCEPT = AGENT_FILE_EXTS.map((e) => `.${e}`).join(',');

export const fileExt = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');
/** The file type of a name (by extension; the server checks the content again). Null when not accepted. */
export function agentFileType(name: string): { ext: AgentFileExt; mime: string; kind: AgentFileKind } | null {
  const ext = fileExt(name);
  if (!(ext in AGENT_FILE_TYPES)) return null;
  const t = AGENT_FILE_TYPES[ext as AgentFileExt];
  return { ext: ext as AgentFileExt, mime: t.mime, kind: t.kind };
}

// ───────────── contracts ─────────────

/** Where the person is in the app: the assistant tailors tips and answers to it. */
export const AgentPage = z.object({
  path: z.string().max(300).default('/'),
  propertyId: zId.optional(),
  section: z.string().max(40).optional(),
  /** Level 3 pages (/einheiten/:unitId/…, …/mietverhaeltnisse/:tenancyId/…): the entity in focus. */
  unitId: zId.optional(),
  tenancyId: zId.optional(),
});
export type AgentPageInput = z.input<typeof AgentPage>;

export const AgentAttachmentBody = z.object({
  name: z.string().trim().min(1).max(200),
  size: z.number().int().positive().max(AGENT_LIMITS.fileMaxBytes, { message: 'Datei größer als 8 MB' }),
  dataBase64: z.string().min(4).max(Math.ceil((AGENT_LIMITS.fileMaxBytes * 4) / 3) + 8),
  conversationId: zId.optional(),
});

export const AgentChatBody = z
  .object({
    conversationId: zId.optional(),
    text: z.string().trim().max(AGENT_LIMITS.textMax).default(''),
    attachmentIds: z.array(zId).max(AGENT_LIMITS.filesPerMessage).default([]),
    /** Answer to a question of the assistant (chip or free text). */
    answer: z.object({ messageId: zId, choice: z.string().trim().max(300), skip: z.boolean().default(false) }).optional(),
    /**
     * Continue after a proposal of this chat was applied (docs/adr/0013): the assistant goes on with the next step of the
     * case without the person typing "weiter". Once per proposal; costs no trial message.
     */
    continue: z.object({ proposalId: zId }).optional(),
    page: AgentPage.default({ path: '/' }),
  })
  .refine((b) => b.text.length > 0 || b.attachmentIds.length > 0 || !!b.answer || !!b.continue, { message: 'Nachricht fehlt', path: ['text'] });
export type AgentChatInput = z.input<typeof AgentChatBody>;

/** Kontonotizen (docs/17 §Gedächtnis): one fact per note, a hard cap per account and in the prompt. */
export const AGENT_NOTE_MAX = 300;
export const AGENT_NOTES_PER_ACCOUNT = 100;
export const AGENT_NOTES_PROMPT_CHARS = 2000;
export const AgentNoteBody = z.object({ text: z.string().trim().min(3).max(AGENT_NOTE_MAX), propertyId: zId.nullable().optional(), tenancyId: zId.nullable().optional() });
export const AgentNotePatchBody = z.object({ text: z.string().trim().min(3).max(AGENT_NOTE_MAX) });
export type AgentNoteDto = { id: string; text: string; propertyId: string | null; tenancyId: string | null; source: 'agent' | 'user'; createdAt: string; updatedAt: string };
/** Rückmeldung zu einer Antwort: Daumen hoch/runter, optional ein Satz dazu. */
export const AgentFeedbackBody = z.object({ rating: z.enum(['up', 'down']), comment: z.string().trim().max(1000).optional() });
export type AgentFeedbackRating = 'up' | 'down';

export const AgentSettingsBody = z.object({
  /** "Direkt eintragen": internal changes without warnings are applied without asking first (always logged, undoable). */
  autoApply: z.boolean().optional(),
  /** Show tips on every page. */
  tips: z.boolean().optional(),
  /** Form of address in drafts that reach tenants (letters, reading requests): Du or Sie. */
  tone: z.enum(['du', 'sie']).optional(),
  /** Proactive help: page greeting, next-step chips and the nightly preparation ("Während du weg warst"). */
  proactive: z.boolean().optional(),
  /** Daily e-mail digest of what the nightly run prepared (default off). */
  digest: z.boolean().optional(),
  /**
   * Belege-Eingang: "Alle sicheren bestätigen" (default off). Only receipts with a known vendor, a strong placement,
   * confidence ≥ 0.95 and an amount within ±15 % of last year; never partial or unknown apportionable lines.
   */
  safeConfirm: z.boolean().optional(),
});
export type AgentTone = 'du' | 'sie';
export type AgentSettingsDto = { autoApply: boolean; tips: boolean; tone: AgentTone; proactive: boolean; digest: boolean; safeConfirm?: boolean };

/** Values the person changed on a proposal card before applying it (field key → value). */
export const AgentProposalEditBody = z.object({ values: z.record(z.string(), z.union([z.string().max(5000), z.number(), z.boolean(), z.null()])) });
export const AgentUndoBody = z.object({ reason: z.string().trim().max(300).optional() });
export const AgentProposalsQuery = z.object({
  status: z.string().optional(),
  conversationId: z.string().optional(),
  from: zDate.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const AGENT_PROPOSAL_STATUSES = ['pending', 'applying', 'applied', 'rejected', 'failed', 'undone'] as const;
export type AgentProposalStatus = (typeof AGENT_PROPOSAL_STATUSES)[number];
/** internal: only changes data in this account; external: reaches other people (messages to tenants) – never automatic. */
export const AGENT_PROPOSAL_RISKS = ['internal', 'external'] as const;
export type AgentProposalRisk = (typeof AGENT_PROPOSAL_RISKS)[number];

/** One editable or read-only row of a proposal card. */
export type AgentProposalField = {
  key: string;
  label: string;
  value: string | number | boolean | null;
  /** How the card shows and edits the value. */
  type: 'text' | 'date' | 'cents' | 'number' | 'boolean' | 'readonly';
  /** Where the value comes from ("Seite 2 des Vertrags"), shown as a hint. */
  source?: string;
  /** The assistant is unsure: the card marks the row (like confidence < 0.9 in the receipt review). */
  unsure?: boolean;
};

export type AgentProposalDto = {
  id: string;
  conversationId: string | null;
  messageId: string | null;
  tool: string;
  title: string;
  summary: string;
  fields: AgentProposalField[];
  warnings: string[];
  risk: AgentProposalRisk;
  status: AgentProposalStatus;
  auto: boolean;
  createdAt: string;
  createdByName: string;
  appliedAt: string | null;
  appliedByName: string | null;
  undoneAt: string | null;
  undoable: boolean;
  error: string | null;
  /** Links to what was created (e.g. the new tenancy). */
  links: { label: string; href: string }[];
};

export type AgentQuestion = { id: string; text: string; why?: string; options: string[]; allowFree: boolean };
export type AgentAttachmentDto = { id: string; name: string; size: number; kind: AgentFileKind; pages?: number | null };
export type AgentMessageDto = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  attachments: AgentAttachmentDto[];
  proposalIds: string[];
  question: AgentQuestion | null;
  /** The answer could not be produced (provider down, budget): the client shows the fallback text. */
  fallback: 'unavailable' | 'limit' | 'limit_month' | null;
  /** `continue`: written by the app after "Übernehmen" (shown as a small line, not as a bubble). */
  kind: 'message' | 'continue';
  /** The server corrected the answer: it claimed a proposal that does not exist. */
  notice?: 'no_proposal' | null;
  /** The person's own feedback on this answer (assistant messages only). */
  feedback?: AgentFeedbackRating | null;
  /** Model and skills of the turn (shown in the protocol, not in the chat). */
  meta?: { model?: string; skills?: string[]; costUsd?: number };
};
export type AgentStatusDto = {
  configured: boolean;
  enabled: boolean;
  consent: { granted: boolean; at: string | null; needsDecision: boolean };
  settings: AgentSettingsDto;
  canWrite: boolean;
  budget: AgentBudgetDto;
  access: AgentAccessDto;
};

/**
 * Budget of the account (docs/17 §Kosten): the monthly budget (calendar month, Europe/Berlin) and a daily soft cap.
 * `messagesLeft` estimates the answers left this month from the account's average cost per answer.
 */
export type AgentBudgetDto = {
  monthLeftUsd: number;
  dayLeftUsd: number;
  messagesLeft: number;
  /** month: used up until `resetsOn`; day: used up for today. */
  exhausted: 'month' | 'day' | null;
  /** First day of the next month (YYYY-MM-01). */
  resetsOn: string;
};

/**
 * Who may chat (docs/17 §Premium): the KI-Assistent add-on (`premium`: add-on or legacy Premium) or an override;
 * otherwise the one-time trial. `buy` tells the panel how the account owner gets the add-on (web price `priceCents`):
 * `checkout` (new web subscription with the add-on), `add` (second item of the running web subscription, prorated),
 * or not at all (null: not the owner, already included, web price not set up). `portal`/`store` came from older
 * servers (legacy Premium). `includedUnits` is 0 since the add-on (2026-10-21).
 */
export type AgentAccessDto = {
  via: 'premium' | 'override' | 'trial' | null;
  trialLeft: number;
  trialTotal: number;
  buy: 'checkout' | 'add' | 'portal' | 'store' | null;
  priceCents: number;
  includedUnits: number;
};

// ───────────── own work (Eigenleistung) ─────────────

/**
 * Work the landlord does himself (garden, stairwell cleaning, winter service): § 1 Abs. 1 Satz 2 BetrKV allows the
 * amount a third party (a company) would charge for the same work, without that company's VAT. Material the landlord
 * bought counts with what he paid. Only the share for areas all tenants may use is apportionable; parts the landlord
 * uses privately (own garden) stay with him.
 *
 * Exact on whole cents: rate × hours is computed in cents, the shared share is applied once at the end (half up).
 */
export type OwnWorkInput = {
  hoursPerYear: number;
  /** What a company charges per hour, net (without VAT), in cents. */
  hourlyRateNetCents: number;
  /** Material and disposal paid by the landlord, in cents (as paid). */
  materialCents?: number;
  /** Share of the work for areas all tenants may use, 0..100 (100 = only shared areas). */
  sharedPct: number;
  /** Months of the year the work covers (default 12; e.g. garden only Mar–Oct still counts as one year). */
  months?: number;
};
export type OwnWorkResult = {
  laborCents: number;
  materialCents: number;
  totalCents: number;
  apportionableCents: number;
  landlordCents: number;
  sharedPct: number;
  /** Plausibility notes the assistant shows (keys `common:agent.ownWork.<id>`). */
  notes: ('rateHigh' | 'rateLow' | 'hoursHigh' | 'noShared')[];
};
export function ownWork(i: OwnWorkInput): OwnWorkResult {
  const hours = Math.max(0, i.hoursPerYear);
  const rate = Math.max(0, Math.round(i.hourlyRateNetCents));
  const material = Math.max(0, Math.round(i.materialCents ?? 0));
  const pct = Math.min(100, Math.max(0, i.sharedPct));
  const labor = Math.round(hours * rate);
  const total = labor + material;
  const apportionable = Math.round((total * pct) / 100);
  const notes: OwnWorkResult['notes'] = [];
  // typical net hourly rates of German gardening / cleaning companies 2025/26: about 25–60 €
  if (rate > 6500) notes.push('rateHigh');
  if (rate > 0 && rate < 1800) notes.push('rateLow');
  if (hours > 400) notes.push('hoursHigh');
  if (pct === 0) notes.push('noShared');
  return { laborCents: labor, materialCents: material, totalCents: total, apportionableCents: apportionable, landlordCents: total - apportionable, sharedPct: pct, notes };
}

/** Shared share of an area in percent (e.g. garden 400 m², of which 100 m² private → 75), one decimal. */
export function sharedAreaPct(totalArea: number, privateArea: number): number {
  if (!(totalArea > 0)) return 0;
  const shared = Math.min(totalArea, Math.max(0, totalArea - Math.max(0, privateArea)));
  return Math.round((shared / totalArea) * 1000) / 10;
}

// ───────────── tips ─────────────

export type AgentTipSeverity = 'overdue' | 'soon' | 'info' | 'hint';
/**
 * A tip in the assistant panel. Texts are i18n keys (`web:agent.tip.<key>.title|body`) with params; `prompt` is the
 * question the panel sends to the assistant when the person taps "Frag mich" (`web:agent.tip.<key>.prompt`).
 */
export type AgentTip = {
  id: string;
  key: string;
  severity: AgentTipSeverity;
  params: Record<string, string | number>;
  href?: string;
  prompt?: boolean;
};

export type AgentTipDeadline = {
  kind: 'statement_deadline' | 'calibration' | 'notice' | string;
  date: ISODate;
  severity: 'overdue' | 'soon' | 'later' | string;
  propertyName: string;
  refId: string;
  /** Statement deadline: first day of the billing period (gives the year). */
  periodFrom?: ISODate;
  /** German title of the deadline (deposit: "Kaution auszahlen: 1.234,56 € an …"). */
  title?: string;
};
export type AgentTipFacts = {
  today: ISODate;
  properties: number;
  deadlines: AgentTipDeadline[];
  documentsReview: number;
  readingsProposed: number;
  meters: number;
  /** The property in focus (property pages). */
  focus?: {
    propertyId: string;
    name: string;
    units: number;
    tenancies: number;
    costsMissing: number;
    readingsMissing: number;
    year: number;
  } | null;
};

/** Page tips: one "Frag mich" hint per section with typical questions (keys `web:agent.page.<section>`). */
export const AGENT_PAGE_SECTIONS = ['uebersicht', 'einheiten', 'vertraege', 'belege', 'zaehler', 'zahlungen', 'abrechnungen', 'umlage', 'dashboard', 'fristen', 'personen'] as const;
export type AgentPageSection = (typeof AGENT_PAGE_SECTIONS)[number];

/** Section of a path for the page tip (property sections and the account-wide pages). */
export function agentSection(path: string): AgentPageSection | null {
  const seg = path.split('?')[0]!.split('/').filter(Boolean);
  if (!seg.length) return 'dashboard';
  if (seg[0] === 'objekte' && seg[1] && seg[1] !== 'neu') {
    const s = seg[2] ?? 'uebersicht';
    const map: Record<string, AgentPageSection> = { akte: 'uebersicht', kosten: 'belege', zeitraeume: 'abrechnungen', import: 'einheiten' };
    const k = map[s] ?? s;
    return (AGENT_PAGE_SECTIONS as readonly string[]).includes(k) ? (k as AgentPageSection) : null;
  }
  const global: Record<string, AgentPageSection> = { belege: 'belege', zaehler: 'zaehler', zahlungen: 'zahlungen', abrechnungen: 'abrechnungen', fristen: 'fristen', personen: 'personen', mietverhaeltnisse: 'vertraege', einheiten: 'einheiten' };
  return global[seg[0]!] ?? null;
}

const RANK: Record<AgentTipSeverity, number> = { overdue: 0, soon: 1, info: 2, hint: 3 };
const fmt = (d: ISODate) => d.split('-').reverse().join('.');

/**
 * What the assistant tells the landlord without asking the AI (free, instant): deadlines that run out, receipts and
 * readings waiting, the year-end reading, missing costs of the property in focus and one hint for the current page.
 * At most `max` tips, most urgent first; a page hint is always kept.
 */
export function agentTips(f: AgentTipFacts, path: string, max = 5): AgentTip[] {
  const tips: AgentTip[] = [];
  const { y, m, d } = parts(f.today);
  if (f.properties === 0) {
    tips.push({ id: 'setup:first', key: 'setupFirst', severity: 'info', params: {}, href: '/objekte/neu', prompt: true });
  }
  for (const x of f.deadlines) {
    if (x.kind === 'statement_deadline') {
      const days = daysInclusive(f.today, x.date) - 1;
      if (x.severity === 'later' && days > 120) continue;
      const year = x.periodFrom ? Number(x.periodFrom.slice(0, 4)) : Number(x.date.slice(0, 4)) - 1;
      tips.push({
        id: `deadline:${x.refId}`,
        key: x.severity === 'overdue' || days < 0 ? 'statementOverdue' : 'statementDue',
        severity: x.severity === 'overdue' || days < 0 ? 'overdue' : days <= 60 ? 'soon' : 'info',
        params: { property: x.propertyName, year, date: fmt(x.date), days: Math.max(0, days) },
        href: `/abrechnungen/${x.refId}`,
        prompt: true,
      });
    } else if (x.kind === 'calibration') {
      tips.push({ id: `calibration:${x.refId}`, key: x.severity === 'overdue' ? 'calibrationOverdue' : 'calibrationDue', severity: x.severity === 'overdue' ? 'overdue' : 'soon', params: { property: x.propertyName, date: fmt(x.date) }, href: '/zaehler', prompt: true });
    } else if (x.kind === 'deposit' && x.severity !== 'later') {
      tips.push({ id: `deposit:${x.refId}:${x.date}`, key: x.severity === 'overdue' ? 'depositOverdue' : 'depositDue', severity: x.severity === 'overdue' ? 'overdue' : 'soon', params: { property: x.propertyName, date: fmt(x.date), what: x.title ?? '' }, href: `/mietverhaeltnisse/${x.refId}/kaution`, prompt: true });
    } else if (x.kind === 'notice') {
      tips.push({ id: `notice:${x.refId}`, key: 'noticeMoveOut', severity: 'soon', params: { property: x.propertyName, date: fmt(x.date) }, href: `/mietverhaeltnisse/${x.refId}`, prompt: true });
    }
  }
  if (f.documentsReview > 0) tips.push({ id: 'documents:review', key: 'documentsReview', severity: 'soon', params: { count: f.documentsReview }, href: '/belege', prompt: false });
  if (f.readingsProposed > 0) tips.push({ id: 'readings:proposed', key: 'readingsProposed', severity: 'soon', params: { count: f.readingsProposed }, href: '/zaehler', prompt: false });
  // year-end reading: from 15 December to 15 January, when there are meters
  const yearEnd = (m === 12 && d >= 15) || (m === 1 && d <= 15);
  if (yearEnd && f.meters > 0) {
    const year = m === 12 ? y : y - 1;
    tips.push({ id: `yearend:${year}`, key: 'yearEndReadings', severity: m === 1 ? 'soon' : 'info', params: { date: `31.12.${year}`, count: f.meters }, href: '/zaehler', prompt: true });
  }
  // January to March: last year's receipts arrive (property tax, insurance, waste, water)
  if (m >= 1 && m <= 3 && f.properties > 0) tips.push({ id: `receipts:${y - 1}`, key: 'collectReceipts', severity: 'hint', params: { year: y - 1 }, href: '/belege', prompt: true });
  if (f.focus) {
    if (f.focus.units === 0) tips.push({ id: `focus:units:${f.focus.propertyId}`, key: 'focusNoUnits', severity: 'info', params: { property: f.focus.name }, href: `/objekte/${f.focus.propertyId}/einheiten`, prompt: true });
    else if (f.focus.tenancies === 0) tips.push({ id: `focus:tenancies:${f.focus.propertyId}`, key: 'focusNoTenancies', severity: 'info', params: { property: f.focus.name }, href: `/objekte/${f.focus.propertyId}/vertraege`, prompt: true });
    if (f.focus.costsMissing > 0) tips.push({ id: `focus:costs:${f.focus.propertyId}:${f.focus.year}`, key: 'focusCostsMissing', severity: 'info', params: { property: f.focus.name, year: f.focus.year, count: f.focus.costsMissing }, href: `/objekte/${f.focus.propertyId}/umlage`, prompt: true });
    if (f.focus.readingsMissing > 0) tips.push({ id: `focus:readings:${f.focus.propertyId}:${f.focus.year}`, key: 'focusReadingsMissing', severity: 'info', params: { property: f.focus.name, year: f.focus.year, count: f.focus.readingsMissing }, href: `/objekte/${f.focus.propertyId}/zaehler`, prompt: true });
  }
  const seen = new Set<string>();
  const sorted = tips
    .filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)))
    .sort((a, b) => RANK[a.severity] - RANK[b.severity]);
  const section = agentSection(path);
  const page: AgentTip[] = section ? [{ id: `page:${section}`, key: `page.${section}`, severity: 'hint', params: {}, prompt: false }] : [];
  return [...sorted.slice(0, Math.max(0, max - page.length)), ...page];
}
